import { useCallback, useRef } from 'react';

const STICKY_ACTIONS_SELECTOR = '[data-column-id="actions"]';

// Incremented around programmatic scroll-into-view so Combobox's
// `closeMenuOnScroll` can ignore the scroll event we just caused
// (otherwise a just-opened dropdown closes on mouse-up — LAX-10244).
let programmaticCellScrollCount = 0;

function beginProgrammaticCellScroll() {
    programmaticCellScrollCount += 1;
}

function endProgrammaticCellScroll() {
    programmaticCellScrollCount = Math.max(0, programmaticCellScrollCount - 1);
}

function isProgrammaticCellScroll() {
    return programmaticCellScrollCount > 0;
}

function isCellFullyVisible(cell: HTMLElement, scrollContainer: HTMLElement): boolean {
    const actionsCol = scrollContainer.querySelector(STICKY_ACTIONS_SELECTOR) as HTMLElement | null;
    const actionsWidth = actionsCol ? actionsCol.getBoundingClientRect().width : 0;
    const containerRect = scrollContainer.getBoundingClientRect();
    const cellRect = cell.getBoundingClientRect();
    const visibleRight = containerRect.right - actionsWidth;
    return (
        cellRect.left >= containerRect.left
        && cellRect.right <= visibleRight
        && cellRect.top >= containerRect.top
        && cellRect.bottom <= containerRect.bottom
    );
}

function findScrollContainer(el: HTMLElement | null): HTMLElement | null {
    let parent = el?.parentElement ?? null;
    while (parent) {
        const { overflowX, overflowY } = getComputedStyle(parent);
        const scrollableX = parent.scrollWidth > parent.clientWidth && overflowX !== 'visible';
        const scrollableY = parent.scrollHeight > parent.clientHeight && overflowY !== 'visible';
        if (scrollableX || scrollableY) {
            return parent;
        }
        parent = parent.parentElement;
    }
    return null;
}

/**
 * Finds the focusable input/combobox element within a cell
 */
function findFocusableElement(cell: HTMLElement): HTMLElement | null {
    // First, check if there's an active element inside the cell
    const activeElement = document.activeElement as HTMLElement | null;
    if (activeElement && cell.contains(activeElement)) {
        // For react-select, find the input inside the select-trigger
        const comboboxControl = activeElement.closest('.select-trigger');
        if (comboboxControl) {
            const reactSelectInput = comboboxControl.querySelector('input') as HTMLInputElement;
            if (reactSelectInput) {
                return reactSelectInput;
            }
        }
        // For regular inputs, return the element itself if it's focusable
        if (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA') {
            return activeElement;
        }
    }

    // If no active element, try to find the first focusable element in the cell
    // Look for react-select combobox first
    const comboboxControl = cell.querySelector('.select-trigger') as HTMLElement | null;
    if (comboboxControl) {
        const reactSelectInput = comboboxControl.querySelector('input') as HTMLInputElement;
        if (reactSelectInput) {
            return reactSelectInput;
        }
    }

    // Look for regular input or textarea
    const input = cell.querySelector('input:not([type="hidden"]), textarea') as HTMLInputElement | null;
    if (input) {
        return input;
    }

    return null;
}

/**
 * Scrolls the cell into view on focus/click so it is fully visible,
 * especially when a sticky column covers the right side.
 * Preserves focus on input/combobox elements inside the cell during scrolling.
 */
function scrollCellIntoView(cellEl: HTMLElement | null) {
    if (!cellEl) return;

    const cell = cellEl.closest('td');
    if (!cell) return;

    const scrollContainer = findScrollContainer(cell);
    if (!scrollContainer) return;

    // Already fully visible: skip scrollIntoView so we don't fire a
    // spurious scroll event that would close an open Combobox menu.
    if (isCellFullyVisible(cell as HTMLElement, scrollContainer)) {
        return;
    }

    // Find the focusable element before scrolling
    const elementToRestore = findFocusableElement(cell as HTMLElement);
    const shouldRestoreFocus = elementToRestore !== null
        && document.activeElement !== elementToRestore;

    beginProgrammaticCellScroll();
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });

    requestAnimationFrame(() => {
        const actionsCol = scrollContainer.querySelector(STICKY_ACTIONS_SELECTOR) as HTMLElement | null;
        const actionsWidth = actionsCol ? actionsCol.getBoundingClientRect().width : 0;
        const containerRect = scrollContainer.getBoundingClientRect();
        const cellRect = cell.getBoundingClientRect();
        const visibleRight = containerRect.right - actionsWidth;

        if (cellRect.right > visibleRight) {
            const overflow = cellRect.right - visibleRight;
            scrollContainer.scrollLeft += overflow;
        }
        if (cellRect.left < containerRect.left) {
            const overflow = containerRect.left - cellRect.left;
            scrollContainer.scrollLeft -= overflow;
        }

        // Restore focus after scrolling completes — only if we actually
        // lost it. Re-focusing a react-select that still has focus can
        // toggle the menu closed.
        if (shouldRestoreFocus && elementToRestore) {
            // Use multiple requestAnimationFrame calls to ensure scroll is complete
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    endProgrammaticCellScroll();
                    // Re-find the element after scrolling (in case DOM changed)
                    const elementAfterScroll = findFocusableElement(cell as HTMLElement);
                    if (elementAfterScroll && document.body.contains(elementAfterScroll)
                        && document.activeElement !== elementAfterScroll) {
                        try {
                            elementAfterScroll.focus();
                        } catch (e) {
                            // Ignore focus errors (e.g., element not focusable)
                        }
                    }
                });
            });
        } else {
            endProgrammaticCellScroll();
        }
    });
}

/**
 * Hook for grid cells to auto-scroll on focus/click so the cell becomes fully visible,
 * accounting for a sticky column on the right.
 */
function useScrollCellIntoView() {
    const cellRef = useRef<HTMLDivElement | null>(null);

    const handleScrollIntoView = useCallback(() => {
        scrollCellIntoView(cellRef.current);
    }, []);

    return { cellRef, scrollCellIntoView: handleScrollIntoView };
}

export { isProgrammaticCellScroll, scrollCellIntoView, useScrollCellIntoView };
